import { toast } from 'react-toastify';
import { getCooperative } from 'services/API/Cooperative';
import { getCulturesByExploitationId } from 'services/API/Exploitation';
import html2canvas from 'html2canvas';

export const chartSvgToPng = (svgDataurl) =>
  new Promise((resolve) => {
    let canvas, ctx, img;
    img = new Image();
    img.src = svgDataurl;
    img.onload = () => {
      const { width, height } = img;
      const scale = 2;
      canvas = document.createElement('canvas');
      canvas.width = width * scale;
      canvas.height = height * scale;
      ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height, 0, 0, width * scale, height * scale);
      img = new Image();
      img.src = canvas.toDataURL('image/png');
      img.onload = () => {
        canvas = document.createElement('canvas');
        canvas.width = width * scale;
        canvas.height = height * scale;
        ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, width * scale, height * scale);
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/jpeg'));
      };
    };
    img.onerror = () => resolve(null);
  });

const chartToPng = async (selector) => {
  const element = document.querySelector(selector);
  if (!element) return null;
  const svg = element.querySelector('svg');
  if (!svg) return null;
  const bbox = svg.getBoundingClientRect();
  if (!svg.getAttribute('width') && bbox.width > 0) {
    svg.setAttribute('width', bbox.width);
    svg.setAttribute('height', bbox.height);
  }
  const serialized = new XMLSerializer().serializeToString(svg);
  const b64 = btoa(unescape(encodeURIComponent(serialized)));
  return await chartSvgToPng(`data:image/svg+xml;base64,${b64}`);
};

const stackedChartToPng = async (selector) => {
  const element = document.querySelector(selector);
  if (!element) return null;
  const container = element.querySelector('.stream-chart__container');
  if (!container) return null;
  const svgElement = container.querySelector('svg');
  const bbox = svgElement.getBoundingClientRect();
  const svgClone = svgElement.cloneNode(true);
  svgClone.setAttribute('width', bbox.width);
  svgClone.setAttribute('height', bbox.height);
  const serialized = new XMLSerializer().serializeToString(svgClone);
  const b64 = btoa(unescape(encodeURIComponent(serialized)));
  const svgPng = await chartSvgToPng(`data:image/svg+xml;base64,${b64}`);
  const legend = element.querySelector('.stream-chart__legend');
  if (!legend) return svgPng;
  const legendCanvas = await html2canvas(legend, {
    backgroundColor: '#ffffff',
    scale: 2,
    useCORS: true,
  });
  const svgImg = new Image();
  svgImg.src = svgPng;
  await new Promise((resolve) => {
    svgImg.onload = resolve;
  });
  const finalCanvas = document.createElement('canvas');
  finalCanvas.width = svgImg.width;
  finalCanvas.height = svgImg.height + legendCanvas.height;
  const ctx = finalCanvas.getContext('2d');
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
  ctx.drawImage(svgImg, 0, 0);
  ctx.drawImage(legendCanvas, 0, svgImg.height);
  return finalCanvas.toDataURL('image/jpeg', 0.95);
};

const handleDownloadPdf = async ({
  resultData,
  effectiveSuggestionKey,
  exploitation,
  sector,
  user,
  culturesColors,
  mapData,
  buildPdfData,
  downloadPdf,
  setPdfLoading,
  setCurrentPieIndex,
  setForcedChartMode,
  setStackedOpen,
  skipSave = false,
}) => {
  try {
    setPdfLoading(true);
    const coopId = localStorage.getItem('cooperative') ?? user?.cooperative?.id;
    const coopResp = await getCooperative(coopId);
    const cooperative = coopResp.data.data;
    const cultureExploitConv = exploitation.cultures;
    const cultureExploitBio = await getCulturesByExploitationId(exploitation.id, true);
    const simulationCultureWithData = [
      ...cultureExploitConv,
      ...cultureExploitBio.data.data,
    ].filter((culture) => culture.isCustomized);

    await new Promise((resolve) => setTimeout(resolve, 800));
    const graphMarge = await chartToPng('#marge-graph');
    document.querySelector('#section-cultures')?.scrollIntoView();
    setStackedOpen(true);
    await new Promise((resolve) => setTimeout(resolve, 800));
    const stackedCharts = { cultures: {}, intercultures: {} };
    stackedCharts.cultures.euros = await stackedChartToPng('#stacked-chart-cultures');
    stackedCharts.intercultures.euros = await stackedChartToPng('#stacked-chart-intercultures');
    setForcedChartMode('hectares');
    await new Promise((resolve) => setTimeout(resolve, 500));
    stackedCharts.cultures.ha = await stackedChartToPng('#stacked-chart-cultures');
    stackedCharts.intercultures.ha = await stackedChartToPng('#stacked-chart-intercultures');
    setForcedChartMode(null);
    setStackedOpen(false);

    const graphPerYear = [];
    const graphInterculturePerYear = [];
    const startYear = resultData.forecastCampaign.startYear;
    const pngPrev = await chartToPng(`#pie-chart-year-${startYear - 1}`);
    graphPerYear.push(pngPrev);
    for (let i = 0; i < resultData.suggest.years.length; i++) {
      setCurrentPieIndex(i);
      await new Promise((resolve) => setTimeout(resolve, 500));
      const png = await chartToPng(`#pie-chart-year-${startYear + i}`);
      graphPerYear.push(png);
      const pngInter = await chartToPng(`#pie-chart-interculture-year-${startYear + i}`);
      graphInterculturePerYear.push(pngInter);
    }
    setCurrentPieIndex(0);

    const pdfData = buildPdfData({
      resultData,
      suggestionKey: effectiveSuggestionKey,
      exploitation,
      user,
      sector,
      cooperative,
      graphPerYear,
      graphInterculturePerYear,
      stackedCharts,
      graphMarge,
      mapData,
      culturesColors,
      culturesExploit: simulationCultureWithData,
    });

    return await downloadPdf({
      pdfData,
      results: resultData.result,
      exploitationName: exploitation?.name ?? 'simulation',
      skipSave,
    });
  } catch (err) {
    console.error('Erreur génération PDF', err);
    toast.error('Erreur lors de la génération du PDF');
    return null;
  } finally {
    setPdfLoading(false);
  }
};

export { handleDownloadPdf, chartToPng };

const usePdfExport = () => {
  return { handleDownloadPdf, chartToPng };
};

export default usePdfExport;
